import React, { useEffect, useId, useState } from 'react';
import { api } from '../api/client';
import { BubbleInput, Field, inputStyle } from './ui/controls';
import { SPEC_FIELDS, type SpecField } from './specSchemas';

function getPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const next = structuredClone(obj);
  const keys = path.split('.');
  let cur: Record<string, unknown> = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    const child = cur[key];
    if (child == null || typeof child !== 'object' || Array.isArray(child)) {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return next;
}

function displayValue(field: SpecField, spec: Record<string, unknown>): string {
  const raw = getPath(spec, field.path);
  if (raw == null) return '';
  if (field.type === 'stringlist' && Array.isArray(raw)) return raw.join(', ');
  if (field.type === 'boolean') return raw ? 'true' : 'false';
  return String(raw);
}

export function SpecForm({
  kind,
  spec,
  onChange,
}: {
  kind: string;
  spec: Record<string, unknown>;
  onChange: (spec: Record<string, unknown>) => void;
}) {
  const fields = SPEC_FIELDS[kind] ?? [];
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const listId = useId();

  useEffect(() => {
    api.dataviewer
      .formSuggestions(kind)
      .then((res) => setSuggestions(res.data.by_path || {}))
      .catch(() => setSuggestions({}));
  }, [kind]);

  if (fields.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
      {fields.map((field) => {
        const datalistId = `${listId}-${field.path}`;
        const opts = suggestions[field.path] || [];
        const value = displayValue(field, spec);
        if (field.type === 'boolean') {
          return (
            <Field key={field.path} label={field.label}>
              <select
                value={value || 'false'}
                onChange={(e) => onChange(setPath(spec, field.path, e.target.value === 'true'))}
                style={inputStyle}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </Field>
          );
        }
        if (field.type === 'tokens') {
          const raw = getPath(spec, field.path);
          const selected = Array.isArray(raw) ? raw.map((item) => String(item)).filter(Boolean) : [];
          return (
            <Field key={field.path} label={field.label}>
              <BubbleInput
                values={selected}
                suggestions={opts}
                placeholder="Type a value and press Enter"
                onChange={(next) => onChange(setPath(spec, field.path, next))}
              />
            </Field>
          );
        }
        return (
          <Field key={field.path} label={field.label}>
            <input
              style={inputStyle}
              list={opts.length ? datalistId : undefined}
              type={field.type === 'number' ? 'number' : 'text'}
              value={value}
              onChange={(e) => {
                let next: unknown = e.target.value;
                if (field.type === 'number') {
                  next = e.target.value === '' ? '' : Number(e.target.value);
                } else if (field.type === 'stringlist') {
                  next = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                }
                onChange(setPath(spec, field.path, next));
              }}
            />
            {opts.length > 0 && (
              <datalist id={datalistId}>
                {opts.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            )}
          </Field>
        );
      })}
    </div>
  );
}
